import _ from 'lodash';
import orComparator from '../../../shared/util/lodash/merge/orComparator';
import AlarmAPI from '../AlarmAPI';
import type { TypedUseQuerySubscriptionOptions } from '@reduxjs/toolkit/query/react';
import { ComposableDevicesManagerStoreInfoResponse } from '../../../service/device/hooks/useDevicesManager';
import { useMemo } from 'react';
import {
  ALARMS_POLLING_INTERVAL,
  AlarmsQueryParamsAPIRequest,
  AlarmState,
  excludedEventIdsStr,
} from '../Alarm.model';
import { ComposableDevicesStoreInfoResponse } from '../../../service/store/hooks/useDevicesOperator';
import { useAppSelector } from '../../store.model';
import { getFeatureToggle } from '../../../shared/featureToggle';
import useCurrentWorkspace from '../../workspace/hooks/useCurrentWorkspace';
import { useParams } from 'react-router-dom';

type Options = TypedUseQuerySubscriptionOptions;

function useAlarmsByDevice(
  args: {
    commanderId?: string;
    device?:
      | ComposableDevicesManagerStoreInfoResponse
      | ComposableDevicesStoreInfoResponse;
    params?: AlarmsQueryParamsAPIRequest;
  },
  options: Options = {}
) {
  const params = useParams();
  const { data: workspaceData, ...responseWorkspaceAPI } = useCurrentWorkspace(
    params.marketId
  );
  const { data: alarmsData, ...responseAlarmAPI } =
    AlarmAPI.useQueryMyIncidentByWorkspaceIdQuery(
      {
        workspaceId: workspaceData?.id ?? '',
        params: {
          state: AlarmState.NEW,
          device: args.commanderId ?? '',
          page_size: 1000,
          excluded_event_ids: excludedEventIdsStr,
          ...args.params,
        },
      },
      {
        skip: !workspaceData?.id,
        pollingInterval: ALARMS_POLLING_INTERVAL,
        ...options,
      }
    );

  const featureToggle = useAppSelector(getFeatureToggle);

  const data = useMemo(
    () =>
      alarmsData?.data
        ?.filter?.((alarm) =>
          args.commanderId ? alarm.deviceId === args.commanderId : true
        )
        .filter(
          (alarm) =>
            !featureToggle.HideDisconnectionAlarms ||
            (featureToggle.HideDisconnectionAlarms &&
              alarm?.event !== '_disconn')
        )
        .filter((alarm) => {
          if (!args.device) return true;
          if (args.device.commanderId !== alarm.deviceId) return false;
          // it means that is not a composable device so is a common device
          if (!args.device.composableZoneMapping) return true;
          const idZones = Object.values(
            args.device?.composableZoneMapping ?? {}
          );
          const controlUnitIds = args.device?.shelfMappings
            ?.filter((shelf) => shelf && idZones.includes(shelf.id + ''))
            .map((shelf) => shelf?.cuid);
          return controlUnitIds?.includes(alarm.cu ?? -1) ?? [];
        }),
    [
      alarmsData,
      args.commanderId,
      args.device,
      featureToggle.HideDisconnectionAlarms,
    ]
  );

  return {
    ..._.mergeWith(responseWorkspaceAPI, responseAlarmAPI, orComparator),
    count: alarmsData?.count ?? 0,
    data,
  };
}

export default useAlarmsByDevice;
